import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { Box, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'

import InputLabel from './InputLabel'

export default function RangeTextField({
  minBound,
  maxBound,
  min,
  max,
  handleRangeChange,
  step = 1,
  minDistance = 0,
  readOnly = false,
  sx,
  usePadding = false,
}) {
  const { t } = useTranslation()

  const [localValue, setLocalValue] = useState([min, max])

  useEffect(() => {
    setLocalValue([min, max])
  }, [min, max])

  const handleRangeChangeFrom = (event) => {
    const newValue = Number(event.target.value)
    setLocalValue([newValue, localValue[1]])

    if (newValue >= minBound && newValue <= localValue[1] - minDistance) {
      handleRangeChange(newValue, localValue[1])
    }
  }

  const handleBlurChangeFrom = (event) => {
    if (localValue[0] < minBound) {
      setLocalValue([minBound, localValue[1]])
    }

    if (localValue[0] > localValue[1] - minDistance) {
      setLocalValue([localValue[1] - minDistance, localValue[1]])
    }
  }

  const handleRangeChangeTo = (event) => {
    const newValue = Number(event.target.value)

    setLocalValue([localValue[0], newValue])
    if (newValue >= localValue[0] + minDistance && newValue <= maxBound) {
      handleRangeChange(localValue[0], newValue)
    }
  }

  const handleBlurChangeTo = (event) => {
    if (localValue[1] > maxBound) {
      setLocalValue([localValue[0], maxBound])
    }

    if (localValue[1] < localValue[0] + minDistance) {
      setLocalValue([localValue[0], localValue[0] + minDistance])
    }
  }

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={sx}>
      <Box sx={{ pl: 1 }}>
        <InputLabel label={t('common.form.min')} />
        <TextField
          data-testid="range-bound-from"
          variant="outlined"
          size="small"
          value={
            usePadding
              ? `${(localValue.length && localValue[0]) || 0}`.padStart(6, '0')
              : (localValue.length && localValue[0]) || 0
          }
          disabled={minBound === maxBound || readOnly}
          onChange={handleRangeChangeFrom}
          onBlur={handleBlurChangeFrom}
          type="number"
          InputProps={{
            inputProps: {
              'data-testid': 'range-bound-from-input',
              min: minBound,
              max: localValue[1] - minDistance,
              step: step,
            },
          }}
          sx={{ width: '12ch' }}
        />
      </Box>
      <Box sx={{ pr: 1 }}>
        <InputLabel label={t('common.form.max')} />
        <TextField
          data-testid="range-bound-to"
          variant="outlined"
          size="small"
          value={
            usePadding
              ? `${(localValue.length && localValue[1]) || 0}`.padStart(6, '0')
              : (localValue.length && localValue[1]) || 0
          }
          disabled={minBound === maxBound || readOnly}
          onChange={handleRangeChangeTo}
          onBlur={handleBlurChangeTo}
          type="number"
          InputProps={{
            inputProps: {
              min: localValue[0] + minDistance,
              max: maxBound,
              step: step,
            },
          }}
          sx={{ width: '12ch' }}
        />
      </Box>
    </Stack>
  )
}

RangeTextField.propTypes = {
  minBound: T.number.isRequired,
  maxBound: T.number.isRequired,
  min: T.number.isRequired,
  max: T.number.isRequired,
  handleRangeChange: T.func.isRequired,
  step: T.number,
  minDistance: T.number,
  readOnly: T.bool,
  sx: T.object,
  usePadding: T.bool,
}
